'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useSession } from '@/lib/auth-client';
import { useQueryClient } from '@tanstack/react-query';
import { useUserReactions, useAddReaction, useRemoveReaction, useEngagementSummary } from '@/lib/hooks';
import { getAvailableReactions } from '@/lib/config/reactions';
import { ReactionDialog } from './ReactionDialog';
import { useReactionDialog } from '@/lib/context/ReactionDialogContext';
export function ReactionDialogManager() {
  const $ = _c(30);
  const {
    data: session
  } = useSession();
  let t0;
  if ($[0] !== session?.user?.id) {
    t0 = session?.user?.id?.toString();
    $[0] = session?.user?.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const currentUserId = t0;
  const {
    state,
    closeDialog
  } = useReactionDialog();
  const queryClient = useQueryClient();
  let t1;
  bb0: {
    if (state.isOpen && state.objectId && state.objectType) {
      let seeded = false;
      if (state.engagementSummary) {
        const engagementKey = ["activities", "engagement", state.objectType, state.objectId];
        const existing = queryClient.getQueryData(engagementKey);
        if (!existing) {
          queryClient.setQueryData(engagementKey, state.engagementSummary);
          seeded = true;
        }
      }
      if (state.userReactions && currentUserId) {
        const activitiesKey = ["activities", "object", state.objectType, state.objectId];
        const existing_0 = queryClient.getQueryData(activitiesKey);
        if (!existing_0) {
          const activities = state.userReactions.map(r => ({
            id: r.id,
            type: "reaction" as const,
            actorId: currentUserId,
            objectId: state.objectId,
            objectType: state.objectType,
            data: {
              reaction_type: r.reactionType
            },
            createdAt: r.createdAt,
            deletedAt: null
          }));
          queryClient.setQueryData(activitiesKey, activities);
          seeded = true;
        }
      }
      t1 = seeded || !state.engagementSummary && !state.userReactions;
      break bb0;
    }
    t1 = false;
  }
  const cacheSeeded = t1;
  const t2 = state.isOpen && cacheSeeded;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: t4
  } = useUserReactions(currentUserId || "", state.objectId || "", state.objectType || "item", t3);
  let t5;
  if ($[4] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const fetchedUserReactions = t5;
  const t6 = state.isOpen && cacheSeeded;
  let t7;
  if ($[6] !== t6) {
    t7 = {
      enabled: t6
    };
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const {
    data: fetchedEngagementSummary
  } = useEngagementSummary(state.objectId || "", state.objectType || "item", t7);
  const addReaction = useAddReaction();
  const removeReaction = useRemoveReaction();
  let t8;
  if ($[8] !== state.objectType) {
    t8 = state.objectType ? getAvailableReactions(state.objectType) : [];
    $[8] = state.objectType;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const availableReactions = t8;
  let t9;
  if ($[10] !== addReaction || $[11] !== currentUserId || $[12] !== fetchedEngagementSummary || $[13] !== fetchedUserReactions || $[14] !== removeReaction || $[15] !== state) {
    t9 = async reactionType => {
      if (!currentUserId || !state.objectId || !state.objectType) {
        return;
      }
      const hasReaction = fetchedUserReactions.includes(reactionType);
      const action = hasReaction ? "remove" : "add";
      let response;
      if (hasReaction) {
        response = await removeReaction.mutateAsync({
          actorId: currentUserId,
          objectId: state.objectId,
          objectType: state.objectType,
          reactionType
        });
      } else {
        response = await addReaction.mutateAsync({
          objectId: state.objectId,
          objectType: state.objectType,
          reactionType
        });
      }
      if (state.onReactionChange && response) {
        let updatedUserReactions;
        if ("userReactions" in response && Array.isArray(response.userReactions)) {
          updatedUserReactions = response.userReactions.map(_temp);
        } else {
          if (action === "add") {
            updatedUserReactions = [...fetchedUserReactions, reactionType];
          } else {
            updatedUserReactions = fetchedUserReactions.filter(r_1 => r_1 !== reactionType);
          }
        }
        const updatedEngagement = response.engagement || fetchedEngagementSummary;
        state.onReactionChange(reactionType, action, updatedUserReactions, updatedEngagement);
      }
    };
    $[10] = addReaction;
    $[11] = currentUserId;
    $[12] = fetchedEngagementSummary;
    $[13] = fetchedUserReactions;
    $[14] = removeReaction;
    $[15] = state;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleReactionClick = t9;
  const isSignedIn = !!session?.user;
  if (!state.isOpen) {
    return null;
  }
  const t10 = addReaction.isPending || removeReaction.isPending;
  let t11;
  if ($[17] !== closeDialog) {
    t11 = open => {
      if (!open) {
        closeDialog();
      }
    };
    $[17] = closeDialog;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== availableReactions || $[20] !== fetchedEngagementSummary || $[21] !== fetchedUserReactions || $[22] !== handleReactionClick || $[23] !== isSignedIn || $[24] !== state.details || $[25] !== state.isOpen || $[26] !== state.title || $[27] !== t10 || $[28] !== t11) {
    t12 = <ReactionDialog availableReactions={availableReactions} userReactions={fetchedUserReactions} engagementSummary={fetchedEngagementSummary} onReactionClick={handleReactionClick} isSignedIn={isSignedIn} isPending={t10} open={state.isOpen} onOpenChange={t11} title={state.title} details={state.details} />;
    $[19] = availableReactions;
    $[20] = fetchedEngagementSummary;
    $[21] = fetchedUserReactions;
    $[22] = handleReactionClick;
    $[23] = isSignedIn;
    $[24] = state.details;
    $[25] = state.isOpen;
    $[26] = state.title;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function _temp(r_0) {
  return r_0.reactionType || r_0;
}